/**
 * Cloudflare Worker Auth Client
 * Handles authentication with the CF Worker OAuth2 backend
 */

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';

// Use the worker URL directly for API calls (more reliable than Vite proxy)
const API_BASE = CF_WORKER_URL ? `${CF_WORKER_URL}/api` : '/api';

// Session storage key for CF auth (shared across environments)
const SESSION_STORAGE_KEY = 'wb_session_id';
const LEGACY_SESSION_KEYS = ['cf_session_id', 'winbox_session_id'];

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  cleanedEmailsCount: number;
  lastCleanedAt: number | null;
  lastGmailRefreshAt?: number | null;
  subscriptionStatus: string;
  currentPlan: string;
  driveConnected?: boolean;
}

export interface SessionResponse {
  user: AuthUser | null;
  accessToken?: string;
  expiresAt?: number;
}

/**
 * Check for session ID in URL (dev mode) and store it
 */
export function captureSessionFromUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('__session');

  console.log('[Auth] Checking URL for session:', {
    url: window.location.href,
    sessionId: sessionId ? 'found' : 'not found'
  });

  if (sessionId) {
    // Store in localStorage for subsequent requests
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    console.log('[Auth] Session ID stored in localStorage');

    // Clean URL
    params.delete('__session');
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);

    return true;
  }

  return false;
}

/**
 * Get stored session ID (for dev mode)
 */
export function getStoredSessionId(): string | null {
  const stored = localStorage.getItem(SESSION_STORAGE_KEY);
  if (stored) return stored;

  for (const legacyKey of LEGACY_SESSION_KEYS) {
    const legacyValue = localStorage.getItem(legacyKey);
    if (legacyValue) {
      // Migrate legacy key to canonical storage.
      localStorage.setItem(SESSION_STORAGE_KEY, legacyValue);
      localStorage.removeItem(legacyKey);
      return legacyValue;
    }
  }

  return null;
}

export function getSessionHeaders(): Record<string, string> {
  const sessionId = getStoredSessionId();
  return sessionId ? { 'X-Session-Id': sessionId } : {};
}

/**
 * Clear stored session ID
 */
export function clearStoredSessionId(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

/**
 * Get the login URL for OAuth
 * Uses full worker URL because this is a browser redirect, not a fetch
 */
export function getLoginUrl(returnUrl?: string): string {
  const url = `${CF_WORKER_URL}/api/auth/login`;
  
  const storedUtms = typeof window !== 'undefined' ? localStorage.getItem('wb_utms') : null;
  
  if (returnUrl || storedUtms) {
    const params = new URLSearchParams();
    if (returnUrl) params.set('returnUrl', returnUrl);
    
    if (storedUtms) {
      try {
        const parsed = JSON.parse(storedUtms);
        Object.keys(parsed).forEach(k => params.set(k, parsed[k]));
      } catch (e) {}
    }
    
    return `${url}?${params.toString()}`;
  }
  
  return url;
}

export function getDriveConnectUrl(returnUrl?: string): string {
  const url = new URL(`${CF_WORKER_URL || window.location.origin}/api/auth/drive/connect`);
  if (returnUrl) {
    url.searchParams.set('returnUrl', returnUrl);
  }

  const sessionId = getStoredSessionId();
  if (sessionId) {
    url.searchParams.set('sessionId', sessionId);
  }

  return url.toString();
}

/**
 * Redirect to login page
 */
export function signIn(returnUrl?: string): void {
  window.location.href = getLoginUrl(returnUrl);
}

/**
 * Sign out and clear session
 */
export async function signOut(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: getSessionHeaders(),
    });
  } catch (error) {
    console.error('Logout error:', error);
  }

  // Clear stored session
  clearStoredSessionId();
  localStorage.clear();
  sessionStorage.clear();

  // Redirect to home
  window.location.href = '/';
}

/**
 * Get current session from CF Worker
 */
export async function getSession(): Promise<SessionResponse> {
  const sessionHeaders = getSessionHeaders();

  console.log('[Auth] Fetching session:', {
    sessionId: Object.keys(sessionHeaders).length > 0 ? 'present' : 'missing',
    apiBase: API_BASE
  });

  try {
    const response = await fetch(`${API_BASE}/auth/session`, {
      method: 'GET',
      credentials: 'include',
      headers: sessionHeaders,
    });

    console.log('[Auth] Session response status:', response.status);

    if (!response.ok) {
      return { user: null };
    }

    const data = await response.json();
    console.log('[Auth] Session data:', { hasUser: !!data.user, hasToken: !!data.accessToken });
    return data;
  } catch (error) {
    console.error('[Auth] Get session error:', error);
    return { user: null };
  }
}

/**
 * Refresh the access token
 */
export async function refreshToken(): Promise<{ accessToken: string; expiresAt: number } | null> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: getSessionHeaders(),
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    console.error('Refresh token error:', error);
    return null;
  }
}

/**
 * Check if CF Worker auth is available
 */
export function isCloudflareAuthAvailable(): boolean {
  return Boolean(CF_WORKER_URL);
}
